import { IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EditorEmptyState } from "@/components/campaign-creation/wizard/EditorEmptyState";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FieldLegend, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type ManualKeywordTarget,
  type ManualProductTarget,
  parseProductIdentifiers,
} from "@/lib/campaign-creation-state";
import { cn } from "@/lib/utils";

export function ManualKeywordTargetEditor({
  value,
  defaultBid,
  onChange,
}: {
  value: ManualKeywordTarget[];
  defaultBid: string;
  onChange: (value: ManualKeywordTarget[]) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const [tab, setTab] = useState<"suggested" | "input" | "library">("suggested");
  const [input, setInput] = useState("");
  const [matchTypes, setMatchTypes] = useState<ManualKeywordTarget["matchType"][]>([
    "BROAD",
    "PHRASE",
    "EXACT",
  ]);
  const add = () => {
    const existing = new Set(
      value.map((target) => `${target.text.toLowerCase()}:${target.matchType}`),
    );
    const additions: ManualKeywordTarget[] = [];
    for (const text of input
      .split(/[\r\n,]+/)
      .map((part) => part.trim())
      .filter(Boolean)) {
      for (const matchType of matchTypes) {
        const key = `${text.toLowerCase()}:${matchType}`;
        if (existing.has(key)) continue;
        existing.add(key);
        additions.push({ text, matchType, bid: defaultBid });
      }
    }
    if (additions.length > 0) onChange([...value, ...additions]);
    setInput("");
  };

  return (
    <div
      className="grid min-h-[360px] grid-cols-2 overflow-hidden border border-line"
      data-slot="manual-keyword-target-editor"
    >
      <div className="flex min-w-0 flex-col border-r border-line">
        <div className="flex h-10 items-end gap-6 border-b border-line px-4 text-xs font-medium">
          {(["suggested", "input", "library"] as const).map((item) => (
            <button
              key={item}
              type="button"
              data-manual-keyword-tab={item}
              className={cn(
                "h-10 border-b-2 pt-1",
                tab === item
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground",
              )}
              onClick={() => setTab(item)}
            >
              {t(`manual.tabs.${item}`)}
            </button>
          ))}
        </div>
        {tab === "input" ? (
          <div className="flex flex-1 flex-col gap-4 p-4">
            <FieldSet>
              <FieldLegend>{t("manual.matchType")}</FieldLegend>
              <div className="flex flex-wrap gap-4">
                {(["BROAD", "PHRASE", "EXACT"] as const).map((matchType) => (
                  <label
                    key={matchType}
                    htmlFor={`manual-keyword-match-${matchType}`}
                    className="flex items-center gap-2 text-xs"
                  >
                    <Checkbox
                      id={`manual-keyword-match-${matchType}`}
                      checked={matchTypes.includes(matchType)}
                      onCheckedChange={(checked) =>
                        setMatchTypes((current) =>
                          checked
                            ? [...new Set([...current, matchType])]
                            : current.filter((item) => item !== matchType),
                        )
                      }
                    />
                    {t(`manual.matches.${matchType}`)}
                  </label>
                ))}
              </div>
            </FieldSet>
            <Textarea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={t("manual.keyword.placeholder")}
              aria-label={t("manual.keyword.inputLabel")}
              className="min-h-32 resize-none"
            />
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground">{t("manual.keyword.maximum")}</span>
              <Button
                type="button"
                size="sm"
                onClick={add}
                disabled={!input.trim() || matchTypes.length === 0 || !defaultBid.trim()}
              >
                {t("actions.add")}
              </Button>
            </div>
          </div>
        ) : (
          <EditorEmptyState label={t(`manual.empty.${tab}`)} />
        )}
      </div>
      <ManualTargetList
        rows={value.map((target, index) => ({
          key: `${target.text}:${target.matchType}`,
          primary: target.text,
          secondary: t(`manual.matches.${target.matchType}`),
          bid: target.bid,
          onBidChange: (bid) =>
            onChange(
              value.map((item, itemIndex) => (itemIndex === index ? { ...item, bid } : item)),
            ),
          onRemove: () => onChange(value.filter((_, itemIndex) => itemIndex !== index)),
        }))}
      />
    </div>
  );
}

export function ManualProductTargetEditor({
  value,
  defaultBid,
  onChange,
}: {
  value: ManualProductTarget[];
  defaultBid: string;
  onChange: (value: ManualProductTarget[]) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const [input, setInput] = useState("");
  const add = () => {
    const existing = new Set(value.map((target) => target.asin));
    const additions = parseProductIdentifiers(input).flatMap((identifier) => {
      const asin = identifier.toUpperCase();
      if (!/^[A-Z0-9]{10}$/.test(asin) || existing.has(asin)) return [];
      existing.add(asin);
      return [{ asin, bid: defaultBid }];
    });
    if (additions.length > 0) onChange([...value, ...additions]);
    setInput("");
  };
  return (
    <div
      className="grid min-h-[320px] grid-cols-2 overflow-hidden border border-line"
      data-slot="manual-product-target-editor"
    >
      <div className="flex flex-col gap-4 border-r border-line p-4">
        <p className="text-xs font-medium">{t("manual.product.input")}</p>
        <Textarea
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder={t("manual.product.placeholder")}
          aria-label={t("manual.product.inputLabel")}
          className="min-h-32 resize-none"
        />
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={add}
            disabled={!input.trim() || !defaultBid.trim()}
          >
            {t("actions.add")}
          </Button>
        </div>
      </div>
      <ManualTargetList
        rows={value.map((target, index) => ({
          key: target.asin,
          primary: target.asin,
          secondary: t("manual.product.asin"),
          bid: target.bid,
          onBidChange: (bid) =>
            onChange(
              value.map((item, itemIndex) => (itemIndex === index ? { ...item, bid } : item)),
            ),
          onRemove: () => onChange(value.filter((_, itemIndex) => itemIndex !== index)),
        }))}
      />
    </div>
  );
}

function ManualTargetList({
  rows,
}: {
  rows: {
    key: string;
    primary: string;
    secondary: string;
    bid: string;
    onBidChange: (bid: string) => void;
    onRemove: () => void;
  }[];
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <div className="flex min-w-0 flex-col">
      <div className="border-b border-line px-4 py-3 text-xs font-medium">
        {t("manual.added", { count: rows.length })}
      </div>
      <div className="flex-1 overflow-y-auto">
        {rows.length > 0 ? (
          rows.map((row) => (
            <div
              key={row.key}
              className="grid grid-cols-[1fr_120px_32px] items-center gap-2 border-b border-line px-4 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-xs">{row.primary}</p>
                <p className="text-[10px] text-muted-foreground">{row.secondary}</p>
              </div>
              <Input
                size="sm"
                inputMode="decimal"
                value={row.bid}
                onChange={(event) => row.onBidChange(event.target.value)}
                aria-label={t("manual.bidFor", { target: row.primary })}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={t("manual.remove", { target: row.primary })}
                onClick={row.onRemove}
              >
                <IconTrash />
              </Button>
            </div>
          ))
        ) : (
          <EditorEmptyState label={t("manual.noneAdded")} />
        )}
      </div>
    </div>
  );
}
